import { useCallback } from "react";
import { Box } from "~/components/Box";
import { EmptyState } from "~/components/EmptyState";
import { Grid } from "~/components/Grid";
import { LazyList } from "~/components/LazyList";
import { Skeleton } from "~/components/Skeleton";
import { AdvanceByTravelerItem, AdvanceByTravelerResponse } from "../types";
import { asCount, asDecimal } from "../utils";
import { TableBodyCell, TableHeaderCell, TableRow } from "./dataTable";
import { PaginatedTableCard } from "./PaginatedTableCard";
import { useClientTable } from "./useClientTable";

export interface AdvanceByTravelerProps {
  data?: AdvanceByTravelerResponse;
  isFetching: boolean;
}

const PAGE_SIZE = 10;

// `#` rank + `name` + `totalFlights` + `avgAdvanceDays`.
const columnsTemplate = "56px minmax(200px, 2fr) minmax(110px, 1fr) minmax(120px, 1fr)";

const searchSelector = (item: AdvanceByTravelerItem): string => item.name;

export const AdvanceByTraveler = ({ data, isFetching }: AdvanceByTravelerProps) => {
  const travelers = data?.travelers ?? [];

  const { pageRows, search, setSearch, page, setPage, totalPages, filteredCount } = useClientTable(
    travelers,
    { searchSelector, pageSize: PAGE_SIZE },
  );

  const renderBody = useCallback(() => {
    return (
      <Box css={{ overflowX: "auto" }}>
        <Grid style={{ gridTemplateColumns: columnsTemplate }}>
          <TableHeaderCell>#</TableHeaderCell>
          <TableHeaderCell>Viajante</TableHeaderCell>
          <TableHeaderCell numeric>Qtd. de voos</TableHeaderCell>
          <TableHeaderCell numeric>Antecedência média</TableHeaderCell>

          <LazyList
            as="div"
            itemAs="div"
            css={{ display: "contents", "& > div": { display: "contents" } }}
            isLoading={isFetching || !data}
            items={pageRows}
            skeletonQuantity={10}
            EmptyComponent={
              <div style={{ gridColumn: "1 / -1" }}>
                <EmptyState>Nenhum viajante encontrado para o período.</EmptyState>
              </div>
            }
            SkeletonComponent={
              <TableRow>
                <TableBodyCell muted>
                  <Skeleton css={{ width: "20px", height: "$5" }} />
                </TableBodyCell>
                <TableBodyCell bold>
                  <Skeleton css={{ width: "70%", height: "$5" }} />
                </TableBodyCell>
                <TableBodyCell numeric>
                  <Skeleton css={{ width: "40px", height: "$5" }} />
                </TableBodyCell>
                <TableBodyCell numeric>
                  <Skeleton css={{ width: "50px", height: "$5" }} />
                </TableBodyCell>
              </TableRow>
            }
            render={(item, index) => (
              <TableRow key={`${item.name}-${index}`}>
                <TableBodyCell muted>{(page - 1) * PAGE_SIZE + index + 1}</TableBodyCell>
                <TableBodyCell bold>{item.name}</TableBodyCell>
                <TableBodyCell numeric>{asCount(item.totalFlights)}</TableBodyCell>
                <TableBodyCell numeric>{asDecimal(item.avgAdvanceDays)} dias</TableBodyCell>
              </TableRow>
            )}
          />
        </Grid>
      </Box>
    );
  }, [isFetching, data, filteredCount, pageRows, page]);

  return (
    <PaginatedTableCard
      title="Antecedência média de compra de passagens aéreas por viajante"
      search={search}
      onSearchChange={setSearch}
      searchPlaceholder="Buscar viajante..."
      page={page}
      totalPages={totalPages}
      onPageChange={setPage}
      hasData={!!data}
    >
      {renderBody()}
    </PaginatedTableCard>
  );
};

AdvanceByTraveler.displayName = "AdvanceByTraveler";
